<script lang="ts" setup>
import type { TBannerConfig } from '~/app/ui/content/feedBanners';

type Breakpoint =
  | 'xs'
  | 'mobile'
  | 'mobileBig'
  | 'tablet'
  | 'tabletBig'
  | 'desktop';

const props = withDefaults(
  defineProps<{
    config: TBannerConfig[];
    width?: number | 'auto' | 'inherit' | '100%';
    align?: 'left' | 'center' | 'right';
    index?: number | null;
    showPlaceholder?: boolean;
    placeholderHeights?: Partial<Record<Breakpoint, number | string>>;
    placeholderVariant?: 'pattern' | 'solid' | 'transparent';
    zIndex?: number;
  }>(),
  {
    align: 'left',
    width: 'auto',
    index: null,
    showPlaceholder: false,
    placeholderHeights: undefined,
    placeholderVariant: 'pattern',
    zIndex: undefined,
  },
);

const { $viewport } = useNuxtApp();
const layoutIndex = computed(() => {
  return $viewport.isGreaterThan('tabletBig')
    ? 0
    : $viewport.isGreaterThan('mobile')
      ? 1
      : 2;
});

const currentConfig = computed(() => props.config[layoutIndex.value]);

const currentSlotKey = computed(() => {
  const config = currentConfig.value;

  if (!config) return '';

  return JSON.stringify({
    ownerId: config.ownerId,
    containerId: config.containerId,
    params: config.params,
    index: props.index,
  });
});

const isError = ref(false);
const isLoaded = ref(false);

const adfoxRef = ref<HTMLDivElement | null>(null);

const containerId = computed(
  () =>
    `${currentConfig.value?.containerId}${props.index != null ? `_${props.index}` : ''}`,
);

const BREAKPOINTS_DESC: Breakpoint[] = [
  'desktop',
  'tabletBig',
  'tablet',
  'mobileBig',
  'mobile',
  'xs',
];

const currentPlaceholderSize = computed(() => {
  const heights = props.placeholderHeights;
  if (!heights) return null;
  for (const bp of BREAKPOINTS_DESC) {
    if (bp in heights && $viewport.isGreaterOrEquals(bp)) {
      return heights[bp] ?? null;
    }
  }
  // fallback: smallest defined breakpoint
  for (const bp of [...BREAKPOINTS_DESC].reverse()) {
    if (bp in heights) return heights[bp] ?? null;
  }
  return null;
});

const placeholderStyle = computed(() => {
  const size = currentPlaceholderSize.value;
  if (size === null) return {};
  if (typeof size === 'string') {
    const [ratio, explicitMaxHeight] = size.split('|');
    const maxHeight = explicitMaxHeight ?? ratio?.split('/')[1];
    return {
      aspectRatio: ratio,
      ...(maxHeight ? { maxHeight: `${maxHeight}px` } : {}),
    };
  }
  return { height: `${size}px` };
});

/*
  Каждая загрузка получает свой id: колбэк из yaContextCb, поставленный
  предыдущей загрузкой или уже размонтированным экземпляром, не должен
  вызывать createAdaptive — adfox ищет контейнер по id и отрендерит баннер
  повторно в актуальный контейнер (дубль баннера, например на 404).
*/
let loadId = 0;
let observer: MutationObserver | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;

const markLoaded = (container: HTMLDivElement) => {
  if (container.offsetHeight > 0 && container.innerHTML.trim().length > 0) {
    isError.value = false;
    isLoaded.value = true;
    observer?.disconnect();
    emit('loaded');
  }
};

const handleAdfoxLoad = async () => {
  if (import.meta.server) return;

  const currentLoadId = ++loadId;
  const container = adfoxRef.value as HTMLDivElement;
  observer?.disconnect();
  isError.value = false;
  isLoaded.value = false;

  if (!container) {
    isError.value = true;
    return;
  }
  container.innerHTML = '';

  await nextTick();
  if (currentLoadId !== loadId) return;

  try {
    window.yaContextCb.push(() => {
      if (currentLoadId !== loadId || !container.isConnected) return;

      window.Ya.adfoxCode.createAdaptive(
        {
          ownerId: currentConfig.value?.ownerId,
          containerId: containerId.value,
          params: currentConfig.value?.params,
          lazyLoad: true,
          onRender: () => {
            if (currentLoadId === loadId) markLoaded(container);
          },
          onError: () => {
            if (currentLoadId !== loadId) return;
            isError.value = true;
            observer?.disconnect();
          },
        },
        ['desktop', 'tablet', 'phone'],
        {
          tabletWidth: 830,
          phoneWidth: 480,
          isAutoReloads: false,
        },
      );
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_: unknown) {
    isError.value = true;
  }

  observer = new MutationObserver(() => {
    setTimeout(() => {
      if (currentLoadId === loadId) markLoaded(container);
    }, 300); // задержка для стабильной вставки
  });
  observer.observe(container, {
    childList: true,
    subtree: true,
  });
};

const checkAndLoadAdfox = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;

  if (!window.yaContextCb || !window.Ya) {
    pollTimer = setTimeout(checkAndLoadAdfox, 100);
  } else {
    handleAdfoxLoad();
  }
};

onMounted(async () => {
  if (import.meta.server) return;
  await nextTick();
  checkAndLoadAdfox();
});

onBeforeUnmount(() => {
  loadId++;
  observer?.disconnect();
  if (pollTimer) clearTimeout(pollTimer);
});

watch(
  () => currentSlotKey.value,
  async () => {
    if (import.meta.client && currentSlotKey.value) {
      await nextTick();
      checkAndLoadAdfox();
    }
  },
);

const emit = defineEmits(['loaded']);
</script>
<template>
  <div
    :class="$style.adfox"
    :style="{
      textAlign: props.align,
      width: typeof props.width === 'number' ? `${props.width}px` : props.width,
    }"
  >
    <div
      v-if="props.showPlaceholder"
      :class="[
        $style.placeholder,
        $style[`placeholder_${props.placeholderVariant}`],
      ]"
      :style="placeholderStyle"
    />

    <div
      v-show="!isError"
      :id="containerId"
      ref="adfoxRef"
      :class="$style.adfox__banner"
      class="adfox__banner"
      :style="props.zIndex != null ? { zIndex: props.zIndex } : undefined"
    />
  </div>
</template>
<style lang="scss" module>
.adfox {
  display: grid;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  &__banner {
    grid-area: 1 / 1;
    z-index: 2;
    justify-self: center;
  }
}

.placeholder {
  grid-area: 1 / 1;
  align-self: start;
  z-index: 1;
  width: 100%;

  &_pattern {
    padding-right: 20px;
    background-color: #e8e8e8;
    background-image: url('./images/banner-placeholder-pattern.svg');
    background-repeat: round repeat;
    background-origin: content-box;
    background-size: 170px 90px;

    @media (max-width: 767px) {
      background-size: 140px 70px;
    }
  }

  &_solid {
    background-color: #ededed;
  }

  &_transparent {
    background-color: transparent;
  }
}
</style>
